import { Listbox } from '@headlessui/react';
import PropTypes from 'prop-types';
import React, { useEffect, useState, forwardRef } from 'react';

import { IconChevronUp, IconChevronDown } from '$partials/icons';

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 * @typedef {Object} ExtendedProps
 * @prop {('concrete'|'outline'|'underline')} [look=concrete] Style variant. Default: 'concrete'
 */
const Select = forwardRef((
    /**@type {Props} */
    {
        defaultLabel,
        filterModal,
        label,
        name,
        onChange = () => { },
        options,
        value = '',
        className,
        ...remainingProps
    },
    /**@type {React.ForwardedRef<HTMLInputElement>} */
    ref
) => {
    const [ selectedOption, setSelectedOption ] = useState(value || '');

    const handleChange = (v) => {
        onChange(v);
        setSelectedOption(v);
    };

    useEffect(() => {
        setSelectedOption(value);
    }, [ value ]);

    const selectedOptionObject = options.find(({ value }) => value === selectedOption);
    const filteredOptions = options.filter((item) => item !== selectedOptionObject);

    let wrapperClasses = 'relative';
    if (name === 'person-alumni-year-filter' && !filterModal) { wrapperClasses = wrapperClasses + ' z-10'; }
    if (name !== 'person-alumni-year-filter' && !filterModal) { wrapperClasses = wrapperClasses + ' z-20'; }

    return (
        <div
            className={wrapperClasses}
            tabIndex={1}>
            {/* Hidden input */}
            <input
                className='sr-only'
                name={name}
                ref={ref}
                type='hidden'
                {...remainingProps}
                value={selectedOption} />

            <Listbox
                onChange={handleChange}
                value={selectedOption}>
                {({ open }) => {
                    // css
                    let buttonClassses = 'relative flex pill-button';
                    if (filterModal) {
                        buttonClassses = buttonClassses + ' pill-button--w-full';
                    }
                    if (open || value !== '') {
                        buttonClassses = buttonClassses + ' pill-button--white-black pill-button--active';
                    }

                    // css: specific customisation
                    if (name === 'person-alumni-year-filter' && !filterModal) { buttonClassses = buttonClassses + ' !pl-60 -ml-60'; }

                    // render
                    return (
                        <div>
                            <Listbox.Button className={buttonClassses}>
                                {options.find(({ value }) => (value === selectedOption))?.label || defaultLabel}

                                <div
                                    aria-hidden='true'
                                    className='pl-10'>
                                    {open && (
                                        <IconChevronUp />
                                    )}
                                    {!open && (
                                        <IconChevronDown />
                                    )}
                                </div>
                            </Listbox.Button>

                            {open && (
                                <div className={`${filterModal ? 'w-full bg-peach' : 'bg-white'} border border-charcoal absolute rounded z-20 top-50 p-20`}>
                                    <Listbox.Options className='flex flex-col gap-y-10'>
                                        {filteredOptions.map((option, idx) => {
                                            return (
                                                <Listbox.Option
                                                    as={React.Fragment}
                                                    key={idx}
                                                    value={option.value}>
                                                    {() => {
                                                        return (
                                                            <li
                                                                className={'type-style-5 self-start cursor-pointer select-none inline-flex items-center py-8 px-12 whitespace-nowrap'}
                                                                key={idx}>
                                                                {option.label}
                                                            </li>
                                                        );
                                                    }}
                                                </Listbox.Option>
                                            );
                                        })}
                                    </Listbox.Options>
                                </div>
                            )}
                        </div>
                    );
                }}
            </Listbox>
        </div>
    );
});

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    onChange: PropTypes.func,
    defaultLabel: PropTypes.string,
    filterModal: PropTypes.bool,
    label: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })).isRequired
};

Select.propTypes = propTypes;
Select.displayName = 'Select';

export default Select;
